// Shared form-field engine. Both the field app (js/app.js) and the admin form
// builder render/collect/validate fields through this one registry, so a form
// behaves the same wherever it is drawn. Handlers receive a `ctx`
// (container/get/set/context/helpers) instead of reaching for a global `state`.
//
//   ctx     = { container, get(id), set(id, value), context?, helpers? }
//   handler = { render?(field, ctx), getValue?(field, ctx), validate?(field, value) }

const _handlers = new Map();

export function register(type, handler) { _handlers.set(type, handler); }
export function has(type) { return _handlers.has(type); }

// Draw the field into ctx.container. Returns false (never throws) when no handler
// is registered, so a caller can fall back / show a placeholder.
export function render(field, ctx) {
  const h = _handlers.get(field.type);
  if (!h || typeof h.render !== 'function') return false;
  h.render(field, ctx);
  return true;
}

// Current value. Defaults to the model value when the handler doesn't override.
export function getValue(field, ctx) {
  const h = _handlers.get(field.type);
  if (h && typeof h.getValue === 'function') return h.getValue(field, ctx);
  return ctx.get(field.id);
}

// null = valid; string = error message. A handler's own validate wins; otherwise
// the default required-empty check applies — this mirrors the universal loop in
// the field app's validateStep(), so composites (which register only `render`)
// keep being required-checked exactly as before.
export function validate(field, value) {
  const h = _handlers.get(field.type);
  if (h && typeof h.validate === 'function') return h.validate(field, value);
  if (field.required) {
    const v = typeof value === 'string' ? value.trim() : value;   // a space is empty
    const empty = v == null || v === '' || (Array.isArray(v) && v.length === 0);
    if (empty) return `Please fill in: ${field.label}`;
  }
  return null;
}

// Test-only: clear the registry between cases.
export function _reset() { _handlers.clear(); }
